import { View } from "react-native";
import { ProfileAvatar } from "app/components/atoms/ProfileAvatar";
import TableList from "app/components/organisms/TableList";
import Pagination from "ui/molecules/pagination/Pagination";
import CardItem from "app/components/organisms/CardItem";
import ResidentContent from "../../residents/organisms/ResidentContent";
import ResidentActions from "../../residents/organisms/ResidentActions";
import DateAdmittedContent from "../../residents/organisms/DateAdmittedContent";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";
import { ColumnModel } from "app/services/api/types/common/models/ColumnModel";
import {
  StatusIndicatorMapping,
  StatusTitleMapping,
} from "app/services/api/types/residents/enums/ResidentStatusEnum";
import StatusIndicator from "ui/atoms/avatar/statusIndicator/StatusIndicator";
import { ResidentsPath } from "../../../constants/routePaths";
import StatusFlagContent from "../../residents/organisms/StatusFlagContent";
import ViewMore from "ui/molecules/common/ViewMore";
import { DependencyIndicator } from "../../residents/atoms/DependencyIndicator";
import usePagination from "ui/molecules/pagination/usePagination";
import { useGetResidentsIndexQuery } from "app/services/api/residentsApi";
import { useLocation } from "../provider/LocationProvider";

interface LocationResidentsListProps {
  locationId: number;
}

function LocationResidentsListContent({
  locationId,
}: LocationResidentsListProps) {
  const { currentPage, handlePageChange } = usePagination();
  const { data, isFetching } = useGetResidentsIndexQuery({
    filter: {
      location_id: locationId,
    },
    page: currentPage,
  });

  const columns: ColumnModel<ResidentModel>[] = [
    {
      title: "Name",
      dataKey: "first_name",
      renderCell: (resident: ResidentModel) => (
        <View className="flex flex-row items-center">
          <ProfileAvatar resident={resident} />
        </View>
      ),
      minWidth: 150,
    },
    {
      title: "Status",
      dataKey: "status",
      renderCell: (resident: ResidentModel) => (
        <StatusIndicator
          status={StatusIndicatorMapping[resident.status]}
          statusTitle={StatusTitleMapping[resident.status]}
        />
      ),
    },
    {
      title: "Dependency",
      dataKey: "dependency",
      renderCell: (resident: ResidentModel) => (
        <View className="flex flex-row items-start">
          <DependencyIndicator dependency={resident.dependency} />
        </View>
      ),
    },
    {
      title: "Status flags",
      dataKey: "status_flags",
      renderCell: (resident: ResidentModel) => (
        <StatusFlagContent resident={resident} />
      ),
    },
    {
      title: "Check in",
      dataKey: "admission_start_date",
      renderCell: (resident: ResidentModel) => (
        <DateAdmittedContent
          date={resident.admission_start_date}
          resident={resident}
        />
      ),
    },
    {
      title: "",
      renderCell: (resident: ResidentModel) => {
        return (
          <View className="flex flex-row items-center justify-end">
            <ViewMore />
          </View>
        );
      },
    },
  ];

  return (
    <View className="flex-1 bg-white">
      <TableList
        columns={columns}
        data={data?.data || []}
        isFetching={isFetching}
        emptyMessage="No residents available for this location."
        clickablePath={(resident) => `${ResidentsPath}/${resident.id}/handover`}
        mobileRenderItem={(item) => (
          <CardItem
            key={item.id}
            actionButtons={<ResidentActions resident={item} />}
            cardContent={<ResidentContent resident={item} />}
            href={`${ResidentsPath}/${item.id}/handover`}
          />
        )}
      />

      <Pagination meta={data?.meta} onPageChange={handlePageChange} />
    </View>
  );
}

export function LocationResidentsList() {
  const { locationId } = useLocation();
  return <LocationResidentsListContent locationId={locationId} />;
}
